import {
  Box,
  Link,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  type Theme,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { ResourceName } from "../../constants/resources";
import type { CrmInventoryUnitFieldsFragment } from "../../generated/graphql";
import { CRM_COLORS, CRM_LINK_SX } from "../../lib/crm";
import { inventoryIdentifier, logisticsInventoryItemPath } from "../../lib/logistics";
import { DeletedInSalesforceBadge } from "../crm/DeletedInSalesforceBadge";
import { InventoryTypeIcon } from "./InventoryTypeIcon";

type InventoryUnit = Omit<CrmInventoryUnitFieldsFragment, "__typename">;

export type InventoryTableRow = {
  key: string;
  inventory: InventoryUnit;
  cells?: ReactNode[];
  deletedInSalesforceAt?: string | null;
};

type InventoryTableProps = {
  label: string;
  rows: InventoryTableRow[];
  columns?: { key: string; label: string; sx?: SxProps<Theme> }[];
  empty?: string;
  maxHeight?: string;
};

const EMPTY = "–";
const BASE_COLUMNS = ["name", "type", "status", "identifier"] as const;
const IDENTIFIER_FIELDS = ["imei", "serialNumber", "hardwareSerialNumber", "simIccid"] as const;
const cellSx = {
  fontSize: "13px",
  py: "8px",
  px: "12px",
  color: CRM_COLORS.text,
  whiteSpace: "nowrap",
  verticalAlign: "top",
} as const;

const InventoryIdentifiers = ({ unit }: { unit: InventoryUnit }) => {
  const translate = useTranslate();
  const primary = inventoryIdentifier(unit);
  const primaryField = IDENTIFIER_FIELDS.find(field => unit[field] && unit[field] === primary);
  const fields = primaryField
    ? [primaryField, ...IDENTIFIER_FIELDS.filter(field => field !== primaryField)]
    : IDENTIFIER_FIELDS;
  const identifiers = fields.flatMap(field => {
    const value = unit[field];
    return value ? [{ field, value }] : [];
  });
  if (identifiers.length === 0) return <>{EMPTY}</>;
  return (
    <Box sx={{ display: "grid", gap: "2px" }}>
      {identifiers.map(({ field, value }) => (
        <Box key={field}>
          <Box component="span" sx={{ color: CRM_COLORS.textWeak, mr: "6px" }}>
            {translate(`crm.contractItem.${field}`)}:
          </Box>
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const InventoryTable = ({ label, rows, columns = [], empty, maxHeight }: InventoryTableProps) => {
  const translate = useTranslate();
  const { canAccess } = useCanAccess({ action: "list", resource: ResourceName.LogisticsInventory });
  const headers = [
    ...BASE_COLUMNS.map(key => ({ key, label: translate(`logistics.inventory.columns.${key}`) })),
    ...columns,
  ];
  if (rows.length === 0)
    return (
      <Typography sx={{ p: "16px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
        {empty ?? translate("logistics.inventory.empty")}
      </Typography>
    );
  return (
    <TableContainer
      role="region"
      aria-label={label}
      tabIndex={0}
      sx={{ maxHeight, backgroundColor: CRM_COLORS.cardBackground }}
    >
      <Table size="small" stickyHeader aria-label={label}>
        <TableHead>
          <TableRow>
            {headers.map(column => (
              <TableCell
                key={column.key}
                sx={{ ...cellSx, backgroundColor: CRM_COLORS.tableHeader, color: CRM_COLORS.textWeak, fontWeight: 700 }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ key, inventory: unit, cells = [], deletedInSalesforceAt }) => (
            <TableRow
              key={key}
              hover
              data-testid="inventory-row"
              sx={{ "&:hover": { backgroundColor: CRM_COLORS.rowHover } }}
            >
              <TableCell sx={cellSx}>
                {canAccess ? (
                  <Link
                    component={RouterLink}
                    to={logisticsInventoryItemPath(unit.id)}
                    sx={{ ...CRM_LINK_SX, fontWeight: 700 }}
                  >
                    {unit.name ?? translate("logistics.inventory.unnamed")}
                  </Link>
                ) : (
                  (unit.name ?? translate("logistics.inventory.unnamed"))
                )}
                {unit.deletedInSalesforceAt && <DeletedInSalesforceBadge deletedAt={unit.deletedInSalesforceAt} />}
                {deletedInSalesforceAt && <DeletedInSalesforceBadge deletedAt={deletedInSalesforceAt} />}
              </TableCell>
              <TableCell sx={cellSx}>
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <InventoryTypeIcon type={unit.type ?? null} size={20} />
                  {unit.type ?? translate("logistics.inventory.noType")}
                </Box>
              </TableCell>
              <TableCell sx={cellSx}>{unit.status ?? EMPTY}</TableCell>
              <TableCell sx={cellSx}>
                <InventoryIdentifiers unit={unit} />
              </TableCell>
              {columns.map((column, index) => (
                <TableCell
                  key={column.key}
                  sx={[cellSx, ...(Array.isArray(column.sx) ? column.sx : [column.sx ?? {}])]}
                >
                  {cells[index]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
